import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import { AppShell } from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import {
  type InviteResult,
  type SchoolUsage,
  fetchSchoolUsage,
  invitePupils,
  parsePupilList,
  revokeSeats,
} from '../lib/school';

// SENCO dashboard at /app/school. Seat summary, bulk invite (paste or CSV),
// and the seat list with per-pupil worksheet counts and a remove button.
// Only school admins (public.school_admins) get data back from /api/school/*.
export function SchoolDashboardScreen() {
  const { isSchoolAdmin } = useAuth();
  const [data, setData] = useState<SchoolUsage | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [listText, setListText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<InviteResult | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);
  const [showRemoved, setShowRemoved] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await fetchSchoolUsage());
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Could not load your school.');
    }
  }, []);

  useEffect(() => {
    if (isSchoolAdmin) void load();
  }, [isSchoolAdmin, load]);

  const parsed = useMemo(() => parsePupilList(listText), [listText]);

  async function onCsv(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setListText((prev) => (prev.trim() ? `${prev.trim()}\n${text}` : text));
    e.target.value = '';
  }

  async function sendInvites() {
    if (!parsed.length) return;
    setBusy(true);
    setActionError(null);
    setResult(null);
    try {
      const r = await invitePupils(parsed);
      setResult(r);
      if (r.invited) setListText('');
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not send invites.');
    } finally {
      setBusy(false);
    }
  }

  async function removeSeat(id: string) {
    setBusy(true);
    setActionError(null);
    try {
      await revokeSeats([id]);
      setConfirmRevoke(null);
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not remove that seat.');
    } finally {
      setBusy(false);
    }
  }

  if (!isSchoolAdmin) {
    return (
      <AppShell title="School" showBack>
        <div className="px-5 py-8">
          <h1 className="text-2xl font-medium text-ink">School dashboard</h1>
          <p className="mt-3 text-sm text-ink/65 leading-relaxed">
            This email isn't set up to manage a school yet. If your school has a licence, sign in with the email
            you gave us, or write to{' '}
            <a href="mailto:support@mydyslexiatools.com" className="text-accent underline">
              support@mydyslexiatools.com
            </a>{' '}
            and we'll sort it out.
          </p>
        </div>
      </AppShell>
    );
  }

  const seats = (data?.seats ?? []).filter((s) => showRemoved || s.status !== 'revoked');
  const removedCount = (data?.seats ?? []).filter((s) => s.status === 'revoked').length;

  return (
    <AppShell title="School" showBack>
      <div className="px-5 pt-2 pb-6 space-y-5">
        <section className="pt-4">
          <p className="text-xs uppercase tracking-wider text-ink/40">School dashboard</p>
          <h1 className="mt-1 text-2xl font-medium text-ink leading-snug">{data?.school.name ?? 'Loading...'}</h1>
          {data?.school.licenceExpiresAt && (
            <p className="mt-1 text-xs text-ink/55">Licence runs until {formatDate(data.school.licenceExpiresAt)}</p>
          )}
        </section>

        {loadError && <p className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-800">{loadError}</p>}

        {data && (
          <section className="grid grid-cols-3 gap-2">
            <Stat label="Pupils using it" value={data.counts.active} />
            <Stat label="Invited" value={data.counts.pending} />
            <Stat label="Seats left" value={data.counts.available} />
          </section>
        )}

        {/* Invite */}
        <section className="rounded-2xl bg-white border border-ink/5 p-4 space-y-3">
          <div>
            <h2 className="text-base font-medium text-ink">Add pupils</h2>
            <p className="mt-1 text-xs text-ink/60 leading-relaxed">
              One pupil per line. Add a first name or class after a comma if it helps you, for example
              "sam@school.org, Sam 5B". Each pupil gets an email with a sign-in link. Pupils who already use
              My Dyslexia Tools are switched over automatically.
            </p>
          </div>
          <textarea
            value={listText}
            onChange={(e) => setListText(e.target.value)}
            rows={5}
            placeholder={'sam@school.org, Sam 5B\nalex@school.org, Alex 6C'}
            className="w-full rounded-xl border border-ink/10 bg-cream px-3 py-2 text-sm text-ink focus:outline-none focus:ring-4 focus:ring-accent/30"
          />
          <div className="flex items-center justify-between gap-3">
            <label className="text-xs text-accent font-medium cursor-pointer hover:underline">
              Upload a CSV
              <input type="file" accept=".csv,text/csv,text/plain" onChange={onCsv} className="hidden" />
            </label>
            <button
              onClick={() => void sendInvites()}
              disabled={busy || !parsed.length}
              className="px-4 py-2 rounded-full bg-accent text-white text-sm font-medium hover:brightness-105 disabled:opacity-40 transition"
            >
              {busy ? 'Sending...' : parsed.length ? `Invite ${parsed.length} ${parsed.length === 1 ? 'pupil' : 'pupils'}` : 'Invite'}
            </button>
          </div>
          {result && <InviteSummary result={result} />}
          {actionError && <p className="text-sm text-red-700">{actionError}</p>}
        </section>

        {/* Seat list */}
        <section className="rounded-2xl bg-white border border-ink/5">
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <h2 className="text-base font-medium text-ink">Pupils</h2>
            {data && <span className="text-xs text-ink/50">{data.counts.worksheets} worksheets done</span>}
          </div>
          {data && !seats.length && <p className="px-4 pb-4 text-sm text-ink/55">No pupils added yet.</p>}
          <ul className="divide-y divide-ink/5">
            {seats.map((s) => (
              <li key={s.id} className="px-4 py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-ink truncate">{s.pupilLabel || s.pupilEmail}</p>
                  {s.pupilLabel && <p className="text-xs text-ink/50 truncate">{s.pupilEmail}</p>}
                  <p className="mt-0.5 text-xs text-ink/55">
                    <StatusPill status={s.status} />
                    {s.status === 'active' && ` ${s.worksheetsUsed} ${s.worksheetsUsed === 1 ? 'worksheet' : 'worksheets'}`}
                    {s.status === 'pending' && ` since ${formatDate(s.invitedAt)}`}
                  </p>
                </div>
                {s.status !== 'revoked' &&
                  (confirmRevoke === s.id ? (
                    <span className="flex gap-1">
                      <button
                        onClick={() => void removeSeat(s.id)}
                        disabled={busy}
                        className="px-3 py-1.5 rounded-full text-xs font-medium bg-red-600 text-white disabled:opacity-40"
                      >
                        Remove
                      </button>
                      <button
                        onClick={() => setConfirmRevoke(null)}
                        className="px-3 py-1.5 rounded-full text-xs font-medium text-ink/60 border border-ink/10"
                      >
                        Keep
                      </button>
                    </span>
                  ) : (
                    <button
                      onClick={() => setConfirmRevoke(s.id)}
                      className="px-3 py-1.5 rounded-full text-xs font-medium text-ink/60 border border-ink/10 hover:border-red-300 hover:text-red-700 transition"
                    >
                      Remove
                    </button>
                  ))}
              </li>
            ))}
          </ul>
          {removedCount > 0 && (
            <button
              onClick={() => setShowRemoved((v) => !v)}
              className="w-full px-4 py-3 text-xs text-accent font-medium border-t border-ink/5 hover:underline"
            >
              {showRemoved ? 'Hide removed pupils' : `Show ${removedCount} removed`}
            </button>
          )}
        </section>

        <p className="text-xs text-ink/50 leading-relaxed">
          You can add a removed pupil again at any time. Their saved worksheets stay on their own device whatever
          happens to their seat.
        </p>
      </div>
    </AppShell>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl bg-white border border-ink/5 p-3 text-center">
      <p className="text-xl font-medium text-ink">{value}</p>
      <p className="mt-0.5 text-[11px] text-ink/55 leading-tight">{label}</p>
    </div>
  );
}

function StatusPill({ status }: { status: 'pending' | 'active' | 'revoked' }) {
  const styles = {
    active: 'bg-green-100 text-green-800',
    pending: 'bg-amber-100 text-amber-800',
    revoked: 'bg-ink/5 text-ink/55',
  }[status];
  const label = { active: 'Using it', pending: 'Invited', revoked: 'Removed' }[status];
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${styles}`}>{label}</span>;
}

function InviteSummary({ result }: { result: InviteResult }) {
  const lines: string[] = [];
  if (result.invited) lines.push(`${result.invited} ${result.invited === 1 ? 'pupil' : 'pupils'} invited.`);
  if (result.alreadySeated.length) lines.push(`Already on your list: ${result.alreadySeated.join(', ')}.`);
  if (result.overLimit.length)
    lines.push(`Not added, no seats left: ${result.overLimit.join(', ')}. Email us to add more seats.`);
  if (result.invalid.length) lines.push(`Not a valid email: ${result.invalid.join(', ')}.`);
  if (result.emailFailed)
    lines.push('Seats are saved, but the invite emails did not send. Pupils can still sign in with their email.');
  return (
    <div className="rounded-xl bg-accentSoft border border-accent/15 p-3 text-xs text-ink/75 space-y-1">
      {lines.map((l) => (
        <p key={l}>{l}</p>
      ))}
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
}
